import React, { useState, useCallback, useEffect, CSSProperties } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface ExpandedViewProps {
  styles: {
    expandedView?: CSSProperties;
    header?: CSSProperties;
    title?: CSSProperties;
    closeButton?: CSSProperties;
    [key: string]: CSSProperties | undefined;
  };
  onClose: () => void;
  children: React.ReactNode;
  buttonId: string;
  title: string;
  /** Optional additional content to show before the children */
  beforeChildren?: React.ReactNode;
}

const ExpandedView: React.FC<ExpandedViewProps> = ({ 
  styles, 
  onClose, 
  children, 
  buttonId,
  title,
  beforeChildren 
}) => {
  const isInWhatsApp = window.location.href.includes('web.whatsapp.com');
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  const updatePosition = useCallback(() => {
    if (isInWhatsApp) {
      const button = document.querySelector(`[data-button-id="${buttonId}"]`);
      if (button) {
        const rect = button.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right
        });
        setIsPositioned(true);
      }
    } else {
      setIsPositioned(true);
    }
  }, [isInWhatsApp, buttonId]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);

    const positionInterval = setInterval(updatePosition, 100);

    return () => {
      window.removeEventListener('resize', updatePosition);
      clearInterval(positionInterval);
    };
  }, [updatePosition]);

  const expandedContent = (
    <div style={{
      ...styles.expandedView,
      ...(isInWhatsApp ? {
        position: 'fixed',
        top: `${position.top}px`,
        right: `${position.right}px`,
        zIndex: 999999,
        opacity: isPositioned ? 1 : 0,
        transition: 'opacity 0.15s ease-in-out',
      } : {})
    }}>
      <div style={styles.header}>
        <h3 style={styles.title}>{title}</h3>
        <button
          style={styles.closeButton}
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      {beforeChildren}
      {children}
    </div>
  );

  if (isInWhatsApp) {
    return createPortal(expandedContent, document.body);
  }

  return expandedContent;
};

export default ExpandedView; 